import { useState, useEffect } from 'react';
import type { VisibilityScore, VisibilityResponse, CompetitorBrand, CompetitorListResponse } from '../types/index.js';
import { apiGet } from '../lib/api.js';

export interface RankedBrand {
  id: string;
  name: string;
  isSelected: boolean;
  scores: VisibilityScore[];
}

export interface UseCompetitorScoresResult {
  rankings: RankedBrand[];
  loading: boolean;
  error: string | null;
}

export function useCompetitorScores(
  tenantId: string | null,
  brandId: string | null,
  brandName: string | null,
): UseCompetitorScoresResult {
  const [rankings, setRankings] = useState<RankedBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !brandId || !brandName) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const base = `/api/v1/tenants/${tenantId}/brands`;

    async function fetchAll() {
      // Fetch competitors
      let competitors: CompetitorBrand[] = [];
      try {
        const compRes = await apiGet<CompetitorListResponse>(
          `${base}/${brandId}/competitors`,
        );
        competitors = compRes.data;
      } catch {
        // If competitors endpoint fails, just show the brand alone
      }

      // Fetch latest visibility for the selected brand
      const brandScores = await apiGet<VisibilityResponse>(
        `${base}/${brandId}/visibility`,
      );

      const result: RankedBrand[] = [
        { id: brandId!, name: brandName!, isSelected: true, scores: brandScores.data },
      ];

      // Fetch visibility for each competitor (parallel)
      const compResults = await Promise.allSettled(
        competitors.map(async (comp) => {
          const res = await apiGet<VisibilityResponse>(
            `${base}/${comp.id}/visibility`,
          );
          return { comp, scores: res.data };
        }),
      );

      for (const r of compResults) {
        if (r.status === 'fulfilled') {
          result.push({
            id: r.value.comp.id,
            name: r.value.comp.name,
            isSelected: false,
            scores: r.value.scores,
          });
        }
      }

      return result;
    }

    fetchAll()
      .then((result) => {
        if (!cancelled) setRankings(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load competitor data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId, brandId, brandName]);

  return { rankings, loading, error };
}
